'use client'

import { useState, useEffect, Suspense } from 'react'
import Link from 'next/link'
import Navbar from '../components/Navbar'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAuth } from '../contexts/AuthContext'
import { authenticatedFetch, storeSessionTokenFrom } from '@/lib/client-fetch'

type UserType = 'parent' | 'student' | 'driver' | 'waitlist'

const SUPPORTED_CAMPUSES = [
  'UT Austin',
  'Texas A&M',
  'UT Dallas',
  'UT San Antonio',
  'UT Arlington',
  'Texas State',
  'UNT',
  'SMU',
  'TCU',
  'Texas Tech',
  'University of Houston',
  'Rice',
  'Baylor',
  'Other',
]

const SUPPORTED_CITIES = ['Katy', 'Houston', 'Sugar Land']

function RegisterPageContent() {
  const searchParams = useSearchParams()
  const [userType, setUserType] = useState<UserType>('parent')

  // Read type from URL query params and set initial user type
  useEffect(() => {
    const typeParam = searchParams.get('type')
    if (typeParam && ['parent', 'student', 'driver', 'waitlist'].includes(typeParam)) {
      setUserType(typeParam as UserType)
    }
  }, [searchParams])

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-ink mb-2">Create Account</h1>
          <p className="text-sm sm:text-base text-ink-soft">Join Deeliver and start delivering meals</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-6 sm:mb-8">
          <p className="text-sm text-amber-800">
            <strong>Service area notice:</strong> Deeliver currently serves the <strong>Sugar Land, Houston, and Katy</strong> areas
            with deliveries to <strong>UT Austin</strong>. If you are outside this area, you can still register and
            we&apos;ll add you to our waitlist to be notified when we expand to your campus or city.
          </p>
        </div>

        {/* User Type Selection */}
        <div className="flex flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
          {(['parent', 'student', 'driver', 'waitlist'] as UserType[]).map((type) => (
            <button
              key={type}
              onClick={() => setUserType(type)}
              className={`px-4 sm:px-6 py-3 rounded-xl font-semibold transition min-h-[44px] flex items-center justify-center ${
                userType === type
                  ? 'bg-ember text-white shadow-md shadow-ember/25'
                  : 'bg-white text-ink border border-ink/15 hover:border-ink/30'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {/* Registration Form */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg shadow-ink/[0.06] border border-cream-deep">
          {userType === 'parent' && <ParentForm />}
          {userType === 'student' && <StudentForm />}
          {userType === 'driver' && <DriverForm />}
          {userType === 'waitlist' && <WaitlistForm />}
        </div>

        <p className="text-ink-soft text-center mt-6">
          Already have an account? <Link href="/login" className="text-ember hover:text-ember-hover hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  )
}

export default function RegisterPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    }>
      <RegisterPageContent />
    </Suspense>
  )
}

function ParentForm() {
  const router = useRouter()
  const { login } = useAuth()
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    zipCode: '',
    city: '',
    studentCampus: '',
    studentName: '',
    studentEmail: '',
    studentPhone: '',
    paymentPreference: 'card'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long')
      setLoading(false)
      return
    }

    try {
      if (!acceptedTerms) {
        setError('You must agree to the Terms and Policies to register.')
        setLoading(false)
        return
      }

      const { confirmPassword, ...dataToSend } = formData
      const response = await authenticatedFetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'parent',
          ...dataToSend
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed')
      }

      setSuccess(true)
      // Native: register returns a session token when it logs the user in
      storeSessionTokenFrom(data)
      // Login automatically and redirect to home
      if (data.userId) {
        login({
          id: data.userId,
          email: formData.email,
          name: formData.name,
          type: 'parent'
        })
      }
      router.push('/')
    } catch (err: any) {
      setError(err.message || 'Failed to register. Please try again.')
      console.error('Registration error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-6">Parent Registration</h2>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Name</label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Email</label>
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Phone</label>
        <input
          type="tel"
          required
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Password <span className="text-red-600">*</span></label>
        <input
          type="password"
          required
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="At least 6 characters"
          minLength={6}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
        <p className="text-gray-600 text-xs mt-1">Password must be at least 6 characters</p>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Confirm Password <span className="text-red-600">*</span></label>
        <input
          type="password"
          required
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          placeholder="Re-enter your password"
          minLength={6}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Zip Code (origin) <span className="text-red-600">*</span></label>
        <input
          type="text"
          required
          value={formData.zipCode}
          onChange={(e) => setFormData({ ...formData, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) })}
          placeholder="e.g., 75001"
          pattern="[0-9]{5}"
          maxLength={5}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
        <p className="text-gray-600 text-xs mt-1">5-digit zip code for matching with local drivers</p>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">City (origin) <span className="text-red-600">*</span></label>
        <select
          required
          value={formData.city}
          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        >
          <option value="">Select your city</option>
          {SUPPORTED_CITIES.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
          <option value="Other">Other</option>
        </select>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Student&apos;s Campus (destination)</label>
        <select
          required
          value={formData.studentCampus}
          onChange={(e) => setFormData({ ...formData, studentCampus: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        >
          <option value="">Select campus</option>
          {SUPPORTED_CAMPUSES.map((campus) => (
            <option key={campus} value={campus}>{campus}</option>
          ))}
        </select>
      </div>

      {/* Student Information Section */}
      <div className="bg-ember-soft/60 border border-ember/20 rounded-lg p-4">
        <h3 className="font-display font-semibold text-ink mb-3">Student Information (destination)</h3>
        <p className="text-gray-600 text-sm mb-4">Please provide your student&apos;s contact information so they can receive delivery updates.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm mb-2 font-medium">
              Student Full Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.studentName}
              onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
              placeholder="Enter student's full name"
              className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm mb-2 font-medium">
              Student Email Address <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              required
              value={formData.studentEmail}
              onChange={(e) => setFormData({ ...formData, studentEmail: e.target.value })}
              placeholder="student@example.com"
              className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm mb-2 font-medium">
              Student Phone Number <span className="text-red-600">*</span>
            </label>
            <input
              type="tel"
              required
              value={formData.studentPhone}
              onChange={(e) => setFormData({ ...formData, studentPhone: e.target.value })}
              placeholder="(555) 123-4567"
              className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Payment Preference</label>
        <select
          required
          value={formData.paymentPreference}
          onChange={(e) => setFormData({ ...formData, paymentPreference: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        >
          <option value="card">Credit/Debit Card</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded text-sm">
          Registration successful!
        </div>
      )}

      {/* Terms of Service Acceptance */}
      <div className="bg-ember-soft/60 border border-ember/20 rounded-lg p-6 mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 w-5 h-5 text-ember border-gray-300 rounded focus:ring-ember"
            required
          />
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              By clicking this button, you agree to our{' '}
              <Link 
                href="/terms" 
                target="_blank"
                className="text-ember underline hover:text-ember-hover font-medium"
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link 
                href="/refund-policy" 
                target="_blank"
                className="text-ember underline hover:text-ember-hover font-medium"
              >
                Refunds & Cancellations Policy
              </Link>
            </p>
          </div>
          </label>
        </div>

      {!acceptedTerms && (
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-lg mb-6">
          Please accept the Terms of Service to proceed with registration.
      </div>
      )}

      <button
        type="submit"
        disabled={loading || !acceptedTerms}
        className="w-full bg-ember text-white py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-md shadow-ember/25 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
      >
        {loading ? 'Registering...' : 'Register'}
      </button>
    </form>
  )
}

function StudentForm() {
  const router = useRouter()
  const { login } = useAuth()
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    campus: '',
    homeCity: '',
    campusAddress: '',
    pickupPoint: '',
    allergies: '',
    preferences: '',
    parentEmail: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long')
      setLoading(false)
      return
    }

    try {
      if (!acceptedTerms) {
        setError('You must agree to the Terms and Policies to register.')
        setLoading(false)
        return
      }

      const { confirmPassword, ...dataToSend } = formData
      const response = await authenticatedFetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'student',
          ...dataToSend
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed')
      }

      setSuccess(true)
      // Native: register returns a session token when it logs the user in
      storeSessionTokenFrom(data)
      // Login automatically and redirect to home
      if (data.userId) {
        login({
          id: data.userId,
          email: formData.email,
          name: formData.name,
          type: 'student'
        })
      }
      router.push('/')
    } catch (err: any) {
      setError(err.message || 'Failed to register. Please try again.')
      console.error('Registration error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-6">Student Registration</h2>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Name</label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Email</label>
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Phone</label>
        <input
          type="tel"
          required
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Password <span className="text-red-600">*</span></label>
        <input
          type="password"
          required
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="At least 6 characters"
          minLength={6}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
        <p className="text-gray-600 text-xs mt-1">Password must be at least 6 characters</p>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Confirm Password <span className="text-red-600">*</span></label>
        <input
          type="password"
          required
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          placeholder="Re-enter your password"
          minLength={6}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Campus (destination)</label>
        <select
          required
          value={formData.campus}
          onChange={(e) => setFormData({ ...formData, campus: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        >
          <option value="">Select campus</option>
          {SUPPORTED_CAMPUSES.map((campus) => (
            <option key={campus} value={campus}>{campus}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Home City (origin) <span className="text-red-600">*</span></label>
        <select
          required
          value={formData.homeCity}
          onChange={(e) => setFormData({ ...formData, homeCity: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        >
          <option value="">Select your home city</option>
          {SUPPORTED_CITIES.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
          <option value="Other">Other</option>
        </select>
        <p className="text-gray-600 text-xs mt-1">The city your family is sending meals from</p>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Campus Address (destination)</label>
        <input
          type="text"
          required
          value={formData.campusAddress}
          onChange={(e) => setFormData({ ...formData, campusAddress: e.target.value })}
          placeholder="e.g., 123 Main St, Dorm Room 456"
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Meetup Point (destination, optional)</label>
        <input
          type="text"
          value={formData.pickupPoint}
          onChange={(e) => setFormData({ ...formData, pickupPoint: e.target.value })}
          placeholder="e.g., UT Central Hub, Near Dorm A"
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Allergies (Optional)</label>
        <textarea
          value={formData.allergies}
          onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
          placeholder="List any food allergies"
          rows={2}
          className="w-full bg-white text-gray-900 border border-gray-300 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember resize-none"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Preferences (Optional)</label>
        <textarea
          value={formData.preferences}
          onChange={(e) => setFormData({ ...formData, preferences: e.target.value })}
          placeholder="Dietary preferences or special instructions"
          rows={2}
          className="w-full bg-white text-gray-900 border border-gray-300 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember resize-none"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded text-sm">
          Registration successful!
        </div>
      )}

      {/* Terms of Service Acceptance */}
      <div className="bg-ember-soft/60 border border-ember/20 rounded-lg p-6 mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 w-5 h-5 text-ember border-gray-300 rounded focus:ring-ember"
            required
          />
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              By clicking this button, you agree to our{' '}
              <Link 
                href="/terms" 
                target="_blank"
                className="text-ember underline hover:text-ember-hover font-medium"
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link 
                href="/refund-policy" 
                target="_blank"
                className="text-ember underline hover:text-ember-hover font-medium"
              >
                Refunds & Cancellations Policy
              </Link>
            </p>
          </div>
          </label>
        </div>

      {!acceptedTerms && (
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-lg mb-6">
          Please accept the Terms of Service to proceed with registration.
      </div>
      )}

      <button
        type="submit"
        disabled={loading || !acceptedTerms}
        className="w-full bg-ember text-white py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-md shadow-ember/25 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
      >
        {loading ? 'Registering...' : 'Register'}
      </button>
    </form>
  )
}

function DriverForm() {
  const router = useRouter()
  const { login } = useAuth()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    zipCode: '',
    vehicleInfo: '',
    capacity: 1
  })

  const [driverLicenseFile, setDriverLicenseFile] = useState<File | null>(null)
  const [driverLicensePreview, setDriverLicensePreview] = useState<string | null>(null)
  const [proofOfInsuranceFile, setProofOfInsuranceFile] = useState<File | null>(null)
  const [proofOfInsurancePreview, setProofOfInsurancePreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'license' | 'insurance') => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file')
        return
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB')
        return
      }
      
      if (type === 'license') {
        setDriverLicenseFile(file)
        setError('')
        // Create preview
        const reader = new FileReader()
        reader.onloadend = () => {
          setDriverLicensePreview(reader.result as string)
        }
        reader.readAsDataURL(file)
      } else {
        setProofOfInsuranceFile(file)
        setError('')
        // Create preview
        const reader = new FileReader()
        reader.onloadend = () => {
          setProofOfInsurancePreview(reader.result as string)
        }
        reader.readAsDataURL(file)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }
    
    // Validate driver's license is uploaded
    if (!driverLicenseFile) {
      setError('Please upload a picture of your driver\'s license')
      return
    }

    // Validate proof of insurance is uploaded
    if (!proofOfInsuranceFile) {
      setError('Please upload proof of insurance')
      return
    }

    // Require agreement to terms and conditions
    if (!acceptedTerms) {
      setError('You must agree to the Driver Terms and Conditions to register.')
      return
    }
    
    setLoading(true)
    setError('')
    
    try {
      // Compress and convert files to base64
      const compressImage = (file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (e) => {
            const img = new Image()
            img.onload = () => {
              const canvas = document.createElement('canvas')
              let width = img.width
              let height = img.height

              // Resize if too large
              if (width > maxWidth) {
                height = (height * maxWidth) / width
                width = maxWidth
              }

              canvas.width = width
              canvas.height = height

              const ctx = canvas.getContext('2d')
              if (!ctx) {
                reject(new Error('Could not get canvas context'))
                return
              }

              ctx.drawImage(img, 0, 0, width, height)
              
              // Convert to base64 with compression
              const base64String = canvas.toDataURL('image/jpeg', quality)
              resolve(base64String)
            }
            img.onerror = reject
            img.src = e.target?.result as string
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
      }

      const base64License = await compressImage(driverLicenseFile)
      const base64Insurance = await compressImage(proofOfInsuranceFile)
      
      const { confirmPassword, ...dataToSend } = formData
      const response = await authenticatedFetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'driver',
          ...dataToSend,
          driverLicensePhoto: base64License,
          proofOfInsurance: base64Insurance,
          routes: [] // Drivers start with no routes - they add them through the routes page
        }),
      })

      let data
      try {
        data = await response.json()
      } catch (jsonError) {
        // Handle non-JSON responses (like 413 errors)
        if (response.status === 413) {
          throw new Error('File size too large. Images have been compressed, but the request is still too large. Please try with smaller images.')
        }
        throw new Error('Registration failed. Please try again.')
      }

      if (!response.ok) {
        throw new Error(data?.error || 'Registration failed')
      }

      setSuccess(true)
      // Native: register returns a session token when it logs the user in
      storeSessionTokenFrom(data)
      // Login automatically and redirect to home
      if (data.userId) {
        login({
          id: data.userId,
          email: formData.email,
          name: formData.name,
          type: 'driver'
        })
      }
      router.push('/')
    } catch (err: any) {
      setError(err.message || 'Failed to register. Please try again.')
      console.error('Registration error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-6">Driver Registration</h2>
      
      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Name</label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Email</label>
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Phone</label>
        <input
          type="tel"
          required
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Password <span className="text-red-600">*</span></label>
        <input
          type="password"
          required
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="At least 6 characters"
          minLength={6}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
        <p className="text-gray-600 text-xs mt-1">Password must be at least 6 characters</p>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Confirm Password <span className="text-red-600">*</span></label>
        <input
          type="password"
          required
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          placeholder="Re-enter your password"
          minLength={6}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Zip Code (origin) <span className="text-red-600">*</span></label>
        <input
          type="text"
          required
          value={formData.zipCode}
          onChange={(e) => setFormData({ ...formData, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) })}
          placeholder="e.g., 75001"
          pattern="[0-9]{5}"
          maxLength={5}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
        <p className="text-gray-600 text-xs mt-1">5-digit zip code - drivers will only see orders from parents in the same zip code</p>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Vehicle Information</label>
        <input
          type="text"
          required
          value={formData.vehicleInfo}
          onChange={(e) => setFormData({ ...formData, vehicleInfo: e.target.value })}
          placeholder="e.g., 2020 Toyota Camry, License: ABC-1234"
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Capacity (Bags)</label>
        <input
          type="number"
          required
          min="1"
          value={formData.capacity}
          onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">
          Driver's License Photo <span className="text-red-600">*</span>
        </label>
        <input
          id="driver-license-upload"
          type="file"
          accept="image/*"
          onChange={(e) => handleFileChange(e, 'license')}
          required
          className="w-full bg-white text-gray-900 border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
        />
        <p className="text-gray-600 text-xs mt-1">Please upload a clear photo of your driver's license (max 5MB)</p>
        {driverLicensePreview && (
          <div className="mt-4">
            <p className="text-gray-700 text-sm mb-2">Preview:</p>
            <img 
              src={driverLicensePreview} 
              alt="Driver's license preview" 
              className="max-w-xs border border-gray-300 rounded-lg"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">
          Proof of Insurance <span className="text-red-600">*</span>
        </label>
        <input
          id="proof-of-insurance-upload"
          type="file"
          accept="image/*"
          onChange={(e) => handleFileChange(e, 'insurance')}
          required
          className="w-full bg-white text-gray-900 border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
        />
        <p className="text-gray-600 text-xs mt-1">Please upload proof of insurance (max 5MB)</p>
        {proofOfInsurancePreview && (
          <div className="mt-4">
            <p className="text-gray-700 text-sm mb-2">Preview:</p>
            <img 
              src={proofOfInsurancePreview} 
              alt="Proof of insurance preview" 
              className="max-w-xs border border-gray-300 rounded-lg"
            />
          </div>
        )}
      </div>

      {/* Terms of Service Acceptance */}
      <div className="bg-ember-soft/60 border border-ember/20 rounded-lg p-6 mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 w-5 h-5 text-ember border-gray-300 rounded focus:ring-ember"
            required
          />
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              By clicking this button, you agree to our{' '}
              <Link 
                href="/terms" 
                target="_blank"
                className="text-ember underline hover:text-ember-hover font-medium"
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link 
                href="/refund-policy" 
                target="_blank"
                className="text-ember underline hover:text-ember-hover font-medium"
              >
                Refunds & Cancellations Policy
              </Link>
            </p>
          </div>
          </label>
        </div>

      {!acceptedTerms && (
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-lg mb-6">
          Please accept the Terms of Service to proceed with registration.
      </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded text-sm">
          Registration successful!
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !acceptedTerms}
        className="w-full bg-ember text-white py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-md shadow-ember/25 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
      >
        {loading ? 'Registering...' : 'Register'}
      </button>
    </form>
  )
}

function WaitlistForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    zipCode: '',
    interestedAs: 'parent' as 'parent' | 'student' | 'driver',
    studentCampus: '',
    city: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await authenticatedFetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'Failed to join waitlist')
      }

      setSuccess(true)
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="text-5xl mb-4">&#127881;</div>
        <h2 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-3">You&apos;re on the waitlist!</h2>
        <p className="text-gray-600">
          We&apos;ll notify you at <strong>{formData.email}</strong> as soon as we expand to your area.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-2">Join the Waitlist</h2>
      <p className="text-sm text-gray-600">
        Not in our current service area? Sign up to be notified when we expand.
      </p>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">I am a... <span className="text-red-600">*</span></label>
        <select
          required
          value={formData.interestedAs}
          onChange={(e) => setFormData({ ...formData, interestedAs: e.target.value as 'parent' | 'student' | 'driver' })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        >
          <option value="parent">Parent</option>
          <option value="student">Student</option>
          <option value="driver">Driver</option>
        </select>
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Full Name <span className="text-red-600">*</span></label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Email <span className="text-red-600">*</span></label>
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Phone <span className="text-red-600">*</span></label>
        <input
          type="tel"
          required
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">Zip Code (origin) <span className="text-red-600">*</span></label>
        <input
          type="text"
          required
          value={formData.zipCode}
          onChange={(e) => setFormData({ ...formData, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) })}
          placeholder="e.g., 75001"
          pattern="[0-9]{5}"
          maxLength={5}
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm mb-2 font-medium">City (origin)</label>
        <input
          type="text"
          value={formData.city}
          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          placeholder="e.g., Dallas, San Antonio"
          className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
        />
      </div>

      {(formData.interestedAs === 'parent' || formData.interestedAs === 'student') && (
        <div>
          <label className="block text-gray-700 text-sm mb-2 font-medium">Campus (destination)</label>
          <select
            value={formData.studentCampus}
            onChange={(e) => setFormData({ ...formData, studentCampus: e.target.value })}
            className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
          >
            <option value="">Select campus</option>
            {SUPPORTED_CAMPUSES.map((campus) => (
              <option key={campus} value={campus}>{campus}</option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-ember text-white py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-md shadow-ember/25 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
      >
        {loading ? 'Joining...' : 'Join Waitlist'}
      </button>
    </form>
  )
}
